import { z } from "zod";
import { DevilsBargainSchema } from "./check-spec.js";
import { ArcBargainGainSchema } from "./narrative-arc.js";
import { MechanicalNoteSchema, ProseSchema } from "./prose.js";
import { BandSchema, DcSchema, DispositionSchema, DocumentKindSchema, EffectSchema, FactIdSchema, FidelitySchema, ModifierValueSchema, NpcRelationshipSchema, PositionSchema, ReliabilitySchema, SituationPressureSchema, ViewPronounsSchema, } from "./primitives.js";
/**
 * The diegetic boundary, expressed as four independently-defined session
 * views (CLAUDE.md Law 2). These are deliberately NOT related by Omit<> —
 * each is its own top-level schema, so no view can accidentally inherit a
 * mechanical field through a shared base type.
 */
// ---------------------------------------------------------------------------
// MechanicalState — full engine truth. Never sent to a model or a client.
// ---------------------------------------------------------------------------
const MechanicalClockStateSchema = z.strictObject({
    name: z.string().min(1),
    filled: z.number().int().min(0),
    size: z.number().int().min(1),
});
const MechanicalResourceStateSchema = z.strictObject({
    name: z.string().min(1),
    value: z.number().int().min(0),
});
export const PendingBargainSchema = z.strictObject({
    /** The turn the offer was made on — the turn whose short delivery it would complete. */
    turn: z.number().int().min(0),
    bargain: DevilsBargainSchema,
    /** An authored bargain's benefit (2026-09-17); absent on the older adjudicator offers. */
    gains: z.array(ArcBargainGainSchema).min(1).optional(),
    /**
     * The turn the engine held the offer open (BARGAIN_HELD, 2026-10-27): it stood through that turn
     * and lapses at the next move that does not answer it. Absent while it was never held.
     */
    held_on: z.number().int().min(0).optional(),
});
export const MechanicalStateSchema = z.strictObject({
    session_id: z.string().min(1),
    turn: z.number().int().min(0),
    seed: z.string().min(1),
    dc: DcSchema,
    modifiers: z.array(z.strictObject({ value: ModifierValueSchema, reason: MechanicalNoteSchema })),
    position: PositionSchema,
    effect: EffectSchema,
    band: BandSchema,
    clocks: z.array(MechanicalClockStateSchema),
    resources: z.array(MechanicalResourceStateSchema),
    /** fact id -> number of times a request for that CORE fact has been blocked. */
    blocked_attempts: z.record(FactIdSchema, z.number().int().min(0)),
    /**
     * The deal on the table (decision 2): an offer made on a COST turn and not
     * yet answered. Cleared by the learner's answer, or by any other move on a
     * later turn (the offer lapses). Optional so older projections and fixtures
     * read as "none".
     */
    pending_bargain: PendingBargainSchema.nullable().optional(),
});
// ---------------------------------------------------------------------------
// Shared building blocks for the two learner-facing views.
// ---------------------------------------------------------------------------
const ViewNpcSchema = z.strictObject({
    id: z.string().min(1),
    name: z.string().min(1),
    role: z.string().min(1),
    /** 19C: "she/her", "he/him" or "they/them", from the bible. The narrator view fills it; the learner view leaves it out. */
    pronouns: ViewPronounsSchema.optional(),
    relationship: NpcRelationshipSchema,
    /** The ordered enum label (see DISPOSITION_ORDER in primitives.ts) — never a numeric index. */
    disposition: DispositionSchema,
});
const ViewDocumentSummarySchema = z.strictObject({
    id: z.string().min(1),
    kind: DocumentKindSchema,
    title: z.string().min(1),
    fidelity: FidelitySchema,
});
const ViewFactSchema = z.strictObject({
    id: FactIdSchema,
    text: ProseSchema,
    reliability: ReliabilitySchema,
});
/**
 * No `turn` field here (deliberately, unlike StateBlock's hypothesis/ruling
 * shapes): a numeric turn index is not itself mechanical — it is not a DC,
 * band, position, effect, or a clock/resource fill value — but keeping the
 * forbidden-value check as `number` (not "number, except turn ordinals")
 * makes the type-level guarantee genuinely absolute rather than
 * quietly-carved-up, which is the whole point of preferring a type that
 * forbids the mistake over a comment that warns about it. Array order
 * conveys sequence instead.
 */
const ViewHypothesisSchema = z.strictObject({
    /** The learner's own words — never Prose-filtered. */
    text: z.string().min(1),
    /** System-generated standing commentary, translated into fiction by the Narrator. */
    status: MechanicalNoteSchema,
});
/** The immutable ruling ledger, read-only to the Narrator. Only finalized rulings appear here. */
const ViewRulingSchema = z.strictObject({
    ruling: ProseSchema,
    final: z.literal(true),
});
// ---------------------------------------------------------------------------
// NarratorView — the Narrator's ENTIRE window onto session state.
// ---------------------------------------------------------------------------
export const NarratorViewSchema = z.strictObject({
    engagement_question: ProseSchema,
    documents: z.array(ViewDocumentSummarySchema),
    facts: z.array(ViewFactSchema),
    npcs: z.array(ViewNpcSchema),
    open_threads: z.array(ProseSchema),
    hypotheses_on_record: z.array(ViewHypothesisSchema),
    ruling_ledger: z.array(ViewRulingSchema),
    /** The ONLY channel by which clock state reaches prose. Never a number. */
    situation_pressure: SituationPressureSchema,
    /**
     * The engagement calendar in words — "day 3 of 12; 9 days remain" — so
     * every mention of time in the prose agrees with the engine's day count.
     * The last day reads "day 8 of 8; this is the last day" until the deadline
     * arrives, then "day 8 of 8; no days remain" (Andre's ruling R7, 2026-10-29).
     * A string by design: the view type forbids numeric fields (Law 2), and
     * the narrator needs to say it, not compute with it.
     */
    calendar: z.string().min(1),
});
// ---------------------------------------------------------------------------
// LearnerViewInPlay — what the client renders during play. Same prohibition
// as NarratorView, plus narration, available documents, and the current beat.
// ---------------------------------------------------------------------------
export const LearnerViewInPlaySchema = z.strictObject({
    engagement_question: ProseSchema,
    narration: z.array(ProseSchema),
    documents: z.array(ViewDocumentSummarySchema),
    facts: z.array(ViewFactSchema),
    npcs: z.array(ViewNpcSchema),
    open_threads: z.array(ProseSchema),
    hypotheses_on_record: z.array(ViewHypothesisSchema),
    ruling_ledger: z.array(ViewRulingSchema),
    situation_pressure: SituationPressureSchema,
    current_beat: z.strictObject({ id: z.string().min(1), title: ProseSchema }),
});
// ---------------------------------------------------------------------------
// LearnerViewAtDebrief — the full tape. Mandatory full mechanical disclosure
// (CLAUDE.md Law 2). This is the one view permitted to carry dc/band/etc.
// ---------------------------------------------------------------------------
const CheckTapeEntrySchema = z.strictObject({
    turn: z.number().int().min(0),
    position: PositionSchema,
    position_reason: MechanicalNoteSchema,
    effect: EffectSchema,
    effect_reason: MechanicalNoteSchema,
    dc: DcSchema,
    modifiers: z.array(z.strictObject({ value: ModifierValueSchema, reason: MechanicalNoteSchema })),
    roll: z.strictObject({
        seed_counter: z.number().int().min(0),
        raw: z.number().int().min(1).max(20),
    }),
    resolved_band: BandSchema,
});
const TruthRevealSchema = z.strictObject({
    mechanism: ProseSchema,
    decoy_claim: ProseSchema,
    fell_for_decoy: z.boolean(),
    fell_for_decoy_at_turn: z.number().int().min(0).nullable(),
});
const CounterfactualRevealSchema = z.strictObject({
    thread: z.string().min(1),
    what_would_have_been_found: ProseSchema,
});
const RubricRecordEntrySchema = z.strictObject({
    assessment_id: z.string().min(1),
    score: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]).nullable(),
    evidence: z.array(z.string().min(1)).max(2),
});
export const LearnerViewAtDebriefSchema = z.strictObject({
    case_id: z.string().min(1),
    checks: z.array(CheckTapeEntrySchema),
    truth_reveal: TruthRevealSchema,
    counterfactuals: z.array(CounterfactualRevealSchema),
    rubric_record: z.array(RubricRecordEntrySchema).length(6),
});
//# sourceMappingURL=views.js.map